import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Tooltip from '../components/tooltip/Tooltip';
import { getProductLineDetail, annulMonetization } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import AnnulMonetizationDialog from '../components/dialogs/AnnulMonetizationDialog';
import GuaranteeAnnulActions from '../components/guarantees/GuaranteeAnnulActions';
import { useToast } from '../components/toast/toast';
import { resolveAnnulInstrumentType } from '../utils/guaranteeAnnulUtils';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';
import {
  currencyDisplayLabel,
  isoCodeForGuaranteeCurrency,
} from '../utils/currencyLabel';

interface Installment {
  id: number;
  installment_number: number;
  due_date: string | null;
  payment_date: string | null;
  amortization: number | null;
  interest: number | null;
  amount: number;
  status_type_id: number;
  status_type_name: string;
}

interface Guarantee {
  id: number;
  id_global: string;
  created_at: string | null;
  issue_date: string | null;
  monetization_date: string | null;
  due_date: string | null;
  creditor_name?: string | null;
  amortization_type_name?: string | null;
  monetization_amount?: number | null;
  net_monetization_amount?: number | null;
  installment_default?: number;
  payment_frequency_type_name?: string | null;
  term_in_days?: number | null;
  installment_current?: number;
  installment_count?: number;
  status_type_name?: string | null;
  monetization_status_type_id?: number | null;
  company_name?: string;
  creditor_id?: number | null;
  bank_operation_number?: string | null;
  amortization_type_id?: number | null;
  account_officer?: string | null;
  loan_number?: string | null;
  payment_frequency_type_id?: number | null;
  observation?: string | null;
  rate_type_id?: number | null;
  rate_type_name?: string | null;
  percentage_point?: number | null;
  grace_period?: number | null;
  weighting_percentage?: number | null;
  weighting_amount?: number | null;
  gdu_weighting_factor_id?: number | null;
  gdu_weighting_factor_name?: string | null;
  weighting_receipt?: string | null;
  installments?: Installment[];
  installment_default_count?: number;
  iva?: number | null;
  general_tax?: number | null;
  guarantee_type?: string;
  guarantee_type_name?: string;
  instrument_type?: string;
  endorsed?: boolean | null;
  banking_entity_id?: number | null;
  estimated_payment_date?: string | null;
  drawer_id?: number | null;
  monetization_discount_rate?: number | null;
  monetization_cvba_code?: string | null;
  monetization_vat?: number | null;
  monetization_interest?: number | null;
  status_type_id?: number | null;
  epromissory_note_id?: number | null;
  settlement_date: string | null;
}

interface ProductLineDetailData {
  product_line_id: number;
  company_name: string;
  cuit: string;
  application_id: number;
  code: string;
  label: string;
  currency_id: number;
  currency_code?: string;
  approved_amount: number;
  total_used_amount: number;
  available_amount: number;
  installment_default_total?: number;
  installment_default_count?: number;
  guarantees: Guarantee[];
}

const ProductLineDetail = () => {
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const { cuit, id } = useParams<{ cuit: string; id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [productLineData, setProductLineData] = useState<ProductLineDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [annulDialogOpen, setAnnulDialogOpen] = useState(false);
  const [selectedGuarantee, setSelectedGuarantee] = useState<Guarantee | null>(null);
  const [annulling, setAnnulling] = useState(false);
  const previousLocationKeyRef = useRef<string | null>(null);
  const previousIdRef = useRef<string | undefined>(undefined);

  // Refrescar datos cuando cambia el id o cuando se vuelve a la página (location.key cambia)
  useEffect(() => {
    if (id) {
      const currentLocationKey = location.key;
      const shouldRefresh = location.state?.shouldRefresh;
      const idChanged = previousIdRef.current !== id;
      const locationKeyChanged = previousLocationKeyRef.current !== null && 
                                 previousLocationKeyRef.current !== currentLocationKey;
      
      // Refrescar si:
      // 1. El id cambió
      // 2. location.key cambió (nueva navegación a la misma página) - solo si ya había una key previa
      // 3. Hay un flag shouldRefresh explícito
      // 4. Es la primera carga (previousLocationKeyRef.current === null)
      if (idChanged || locationKeyChanged || shouldRefresh || previousLocationKeyRef.current === null) {
        previousIdRef.current = id;
        previousLocationKeyRef.current = currentLocationKey;
        fetchProductLineDetail();
        
        // Limpiar el state si había un flag shouldRefresh
        if (shouldRefresh) {
          // Usar setTimeout para limpiar el state después de que se procese
          setTimeout(() => {
            window.history.replaceState({}, '', location.pathname);
          }, 100);
        }
      }
    }
  }, [id, location.key, location.state?.shouldRefresh]);

  const fetchProductLineDetail = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProductLineDetail(id);
      
      if (response.success && 'data' in response) {
        // Verificar que guarantees sea un array válido
        if (!response.data.guarantees) {
          response.data.guarantees = [];
        } else if (!Array.isArray(response.data.guarantees)) {
          response.data.guarantees = [];
        } else {
          // Filtrar elementos nulos o undefined del array
          const validGuarantees = response.data.guarantees.filter((g: any) => g != null);
          response.data.guarantees = validGuarantees;
        }
        
        setProductLineData(response.data);
      } else {
        setError('Error al cargar los datos de la línea de producto');
        navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
      }
    } catch (err) {
      setError('Error al cargar los datos de la línea de producto');
      navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMonetization = () => {
    navigate(`${routes.newMonetization}?product_line_id=${id}`);
  };

  const handleGoBack = () => {
    navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
  };

  const handleViewClicked = (row: Guarantee) => {
    if (!productLineData) return;

    const resolveRouteByInstrumentType = (type: string | undefined): string => {
      const normalizedType = type?.trim().toLowerCase().replace(/-/g, '_');

      if (normalizedType === 'loan') return routes.loanDetail;
      if (normalizedType === 'check') return routes.checkDetail;
      if (normalizedType === 'promissory_note') return routes.promissoryNoteDetail;
      if (normalizedType === 'commercial_endorsement') return routes.commercialEndorsementDetail;

      if (productLineData.code === '003') return routes.loanDetail;
      if (productLineData.code === '001' || productLineData.code === '002') return routes.checkDetail;
      if (productLineData.code === '006' || productLineData.code === '007') return routes.promissoryNoteDetail;
      if (productLineData.code === '004') return routes.commercialEndorsementDetail;

      return routes.loanDetail;
    };

    const targetRoute = resolveRouteByInstrumentType(row.instrument_type || row.guarantee_type)
      .replace(':cuit', cuit || '')
      .replace(':lineId', id || '')
      .replace(':id', row.id_global.toString());

    navigate(targetRoute, {
      state: {
        from: location.pathname,
        guaranteeCode: productLineData.code,
        lineContext: {
          approvedAmount: productLineData.approved_amount,
          totalUsedAmount: productLineData.total_used_amount,
          availableAmount: productLineData.available_amount,
          productLineLabel: productLineData.label,
        },
      },
    });
  };

  const resolveRowInstrumentType = (row: Guarantee): string => {
    if (row.guarantee_type || row.instrument_type) {
      return resolveAnnulInstrumentType(row.guarantee_type || row.instrument_type);
    }
    if (!productLineData) return 'check';
    const code = productLineData.code;
    if (code === '003') return 'loan';
    if (code === '001' || code === '002') return 'check';
    if (code === '006' || code === '007') return 'promissory-note';
    if (code === '004') return 'commercial-endorsement';
    return 'check';
  };

  const handleAnnulClick = (row: Guarantee) => {
    setSelectedGuarantee(row);
    setAnnulDialogOpen(true);
  };

  const handleAnnulConfirm = async () => {
    if (!selectedGuarantee || !productLineData) return;

    setAnnulling(true);
    try {
      const type = resolveRowInstrumentType(selectedGuarantee);
      const response = await annulMonetization(type, selectedGuarantee.id);
      if (response.success) {
        setAnnulDialogOpen(false);
        setSelectedGuarantee(null);
        useToast.success({
          title: 'Aval anulado correctamente',
          description: 'El monto fue liberado y ya está disponible nuevamente.',
          id: 'annul-aval-success',
          closeButton: true,
          autoClose: 5000,
          toastId: 'annul-aval-success',
        });
        fetchProductLineDetail();
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'No se pudo anular el aval';
      useToast.error({
        title: 'Error al anular el aval',
        description: message,
        id: 'annul-aval-error',
        closeButton: true,
        autoClose: 7000,
        toastId: 'annul-aval-error',
      });
    } finally {
      setAnnulling(false);
    }
  };

  const renderGuaranteeActions = (row: Guarantee) => (
    <Card display='flex' gap='8px' alignItems='center'>
      <Icon
        name='view_outline'
        variant='info'
        modifier='regular'
        width={24}
        height={24}
        cursor='pointer'
        onClick={() => handleViewClicked(row)}
      />
      <GuaranteeAnnulActions
        monetizationStatusTypeId={row.monetization_status_type_id}
        onAnnulClick={() => handleAnnulClick(row)}
      />
    </Card>
  );

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('es-AR')}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    
    // Parse the date as local time to avoid timezone issues
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-AR');
  };

  const getCurrencyCode = () =>
    isoCodeForGuaranteeCurrency(
      {
        currencyId: productLineData?.currency_id,
        currencyCode: productLineData?.currency_code,
      },
      localCurrencyCode
    );

  const getCurrencyDisplay = () => currencyDisplayLabel(getCurrencyCode());

  const getTypeHeader = () => {
    if (!productLineData) return '';
    
    const guaranteeType = productLineData.code;
    
    if (guaranteeType === '003') {
      return `Prestamos ${getCurrencyDisplay()}`;
    } else {
      return productLineData.label;
    }
  };

  const getTableTitle = () => {
    if (!productLineData) return '';
    
    const guaranteeType = productLineData.code;
    
    switch (guaranteeType) {
      case '003':
        return 'Creditos asociados';
      case '001':
      case '002':
        return 'Cheques asociados';
      case '006':
      case '007':
        return 'Pagare asociados';
      case '004':
        return 'Avales comerciales asociados';
      default:
        return 'Garantías asociadas';
    }
  };

  const getTableColumns = () => {
    if (!productLineData) return [];
    
    const guaranteeType = productLineData.code;
    
    if (guaranteeType === '003') {
      // Prestamo columns - Columnas originales del prompt
      return [
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.id_global}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. creación</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.created_at)}
            </P>
          ),
          minWidth: '110px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de emisión</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.issue_date)}
            </P>
          ),
          minWidth: '110px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. vencimiento</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.due_date)}
            </P>
          ),
          minWidth: '130px',
          grow: 1.3
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acreedor</P>, 
          selector: (row: Guarantee) => row.creditor_name || '-',
          cell: (row: Guarantee) => (
            <Tooltip position='top' msg={row.creditor_name || '-'}>
              <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
                {row.creditor_name || '-'}
              </P>
            </Tooltip>
          ),
          minWidth: '100px',
          grow: 0.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Sist. Amortización</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.amortization_type_name || '-'}
            </P>
          ),
          minWidth: '170px',
          grow: 1.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Bruto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Neto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.net_monetization_amount ? formatCurrency(row.net_monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Mora</P>, 
          selector: (row: Guarantee) => (
            <P 
              variant='secondary-2'
              type="3" 
              weight="regular" 
              withMargin={false}
            >
              {row.installment_default ? formatCurrency(row.installment_default) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Periodicidad</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.payment_frequency_type_name || '-'}
            </P>
          ),
          minWidth: '130px',
          grow: 1.5
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Plazo</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.term_in_days || '-'}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Cuota</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {(row.installment_current !== null && row.installment_current !== undefined && 
                row.installment_count !== null && row.installment_count !== undefined) 
                ? `${row.installment_current} de ${row.installment_count}` 
                : '-'}
            </P>
          ),
          minWidth: '100px',
          grow: 1.2
        },
        { 
          name: '', 
          selector: (row: Guarantee) => row.id,
          cell: (row: Guarantee) => renderGuaranteeActions(row),
          minWidth: '80px',
          grow: 0.6
        }
      ];
    } else if (guaranteeType === '004') {
      // Aval Comercial columns - Similar a Cheque y Pagaré
      return [
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.id_global}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Socio partícipe / Tercero</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.company_name || productLineData.company_name}
            </P>
          ),
          minWidth: '180px',
          grow: 2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de emisión</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.issue_date)}
            </P>
          ),
          minWidth: '130px',
          grow: 1.3
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de vencimiento</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.due_date)}
            </P>
          ),
          minWidth: '160px',
          grow: 1.6
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acreedor</P>, 
          selector: (row: Guarantee) => row.creditor_name || '-',
          cell: (row: Guarantee) => (
            <Tooltip position='top' msg={row.creditor_name || '-'}>
              <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
                {row.creditor_name || '-'}
              </P>
            </Tooltip>
          ),
          minWidth: '100px',
          grow: 0.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Estado</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.status_type_name || '-'}
            </P>
          ),
          minWidth: '100px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Monto bruto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: '', 
          selector: (row: Guarantee) => row.id,
          cell: (row: Guarantee) => renderGuaranteeActions(row),
          minWidth: '80px',
          grow: 0.6
        }
      ];
    } else {
      // Cheque and Pagare columns - Columnas originales del prompt
      return [
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.id_global}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Socio partícipe / Tercero</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.company_name || productLineData.company_name}
            </P>
          ),
          minWidth: '180px',
          grow: 2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de emisión</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.issue_date)}
            </P>
          ),
          minWidth: '130px',
          grow: 1.3
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de monetización</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.monetization_date)}
            </P>
          ),
          minWidth: '160px',
          grow: 1.6
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de liquidación</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.settlement_date)}
            </P>
          ),
          minWidth: '150px',
          grow: 1.5
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acreedor</P>, 
          selector: (row: Guarantee) => row.creditor_name || '-',
          cell: (row: Guarantee) => (
            <Tooltip position='top' msg={row.creditor_name || '-'}>
              <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
                {row.creditor_name || '-'}
              </P>
            </Tooltip>
          ),
          minWidth: '100px',
          grow: 0.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Estado</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.status_type_name || '-'}
            </P>
          ),
          minWidth: '100px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Monto bruto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: '', 
          selector: (row: Guarantee) => row.id,
          cell: (row: Guarantee) => renderGuaranteeActions(row),
          minWidth: '80px',
          grow: 0.6
        }
      ];
    }
  };

  const renderSummaryCard = (title: string, amount: number, hasMora: boolean = false, moraAmount?: number) => (
    <Card variant='neutral' modifier='regular' spacing='4'>
        <H variant="secondary-2" withMargin={false} type='4' weight='bold' modifier='strong'>
            {title}
        </H>
        <Card display='flex' alignItems='center' gap='8px'>
            <H withMargin={false} variant='secondary-2' type="2" weight="bold" modifier='strong'>
                {getCurrencyCode()} {formatCurrency(amount)}
            </H>
            {hasMora && moraAmount && moraAmount > 0 && (
                <Card display='flex' alignItems='center' gap='8px'>
                    <H variant='danger' type="3" weight="bold" withMargin={false}>
                    {getCurrencyCode()} {formatCurrency(moraAmount)}
                    </H>
                    <P variant='neutral' type="1" modifier='weak' withMargin={false}>
                    Mora
                    </P>
                </Card>
            )}
        </Card>
    </Card>
  );

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={4} />
        </Card>
      </Card>
    );
  }

  if (error || !productLineData) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Línea de producto no encontrada'}</P>
        </Card>
      </Card>
    );
  }

  const isChequeOrPagare = ['001', '002', '006', '007', '004'].includes(productLineData.code);
  const isLoan = productLineData.code === '003';

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a gestión de avales"
        />
        <Card display='flex' alignItems='baseline' gap='8px'>
            <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
            Linea {productLineData.product_line_id} 
            </H>
            <H variant="secondary-2" type="3" weight="bold" modifier="strong">
            {getTypeHeader()}
            </H>
        </Card>
      </Card>

      {/* Input fields row */}
      <div style={{ display: 'grid', gridTemplateColumns: isChequeOrPagare ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div>
          <Input
            label="Razón social"
            value={productLineData.company_name}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        <div>
          <Input
            label={fiscalId.label}
            value={fiscalId.format(productLineData.cuit)}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        <div>
          <Input
            label="Tipo de operación"
            value={productLineData.label}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        <div>
          <Input
            label="Tipo de garantía"
            value={productLineData.guarantees?.[0]?.guarantee_type || '-'}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        {isChequeOrPagare && (
          <div>
            <Input
              label="Divisa"
              value={getCurrencyDisplay()}
              readOnly
              size="md"
              modifier="weak"
            />
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isLoan ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', 
        gap: '16px', 
        marginBottom: '48px' 
      }}>
        {renderSummaryCard('Total', productLineData.approved_amount)}
        {renderSummaryCard('Utilizado', productLineData.total_used_amount)}
        {renderSummaryCard('Disponible', productLineData.available_amount)}
        {isLoan && (
          renderSummaryCard(
            'Cuotas afrontadas', 
            productLineData.installment_default_count || 0, 
            true,
            productLineData.installment_default_total ? productLineData.installment_default_total : undefined
          )
        )}
      </div>

      {/* Table section header */}
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {getTableTitle()}
        </H>
        <Button variant='secondary' icon='plus_icon' iconAlign='left' onClick={handleNewMonetization}>
          Nueva monetización
        </Button>
      </Card>

      {/* Table */}
      <Card margin={{ top: '3' }}>
        {productLineData.guarantees && productLineData.guarantees.length > 0 ? (
          <Table
            columns={getTableColumns()}
            data={productLineData.guarantees}
            tableData={{ 
              page: 0, 
              size: productLineData.guarantees.length, 
              totalElements: productLineData.guarantees.length, 
              totalPages: 1 
            }}
            handleChangePage={() => {}}
          />
        ) : (
          <Card
            display='grid'
            justifyContent='center'
            margin={{ top: '11', bottom: '11' }}
            width='100%'
          >
            <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
              <P variant='primary' align='center' withMargin={false} weight='bold'>
                No se encontraron registros
              </P>
            </Card>
          </Card>
        )}
      </Card>

      <AnnulMonetizationDialog
        isOpen={annulDialogOpen}
        onClose={() => { setAnnulDialogOpen(false); setSelectedGuarantee(null); }}
        onConfirm={handleAnnulConfirm}
        guarantee={selectedGuarantee && productLineData ? {
          id_global: selectedGuarantee.id_global,
          monetization_amount: selectedGuarantee.monetization_amount,
          application_product_line_id: Number(id),
          product_label: productLineData.label,
          currency_code: getCurrencyCode(),
        } : null}
        isLoading={annulling}
      />
    </Card>
  );
};

export default ProductLineDetail;